import { t } from "@brouhaha/i18n";
import { Button, Card, Timer, useCountdown } from "@brouhaha/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId } from "react";

import { useWakeLock } from "../lib/use-wake-lock";

/**
 * The parts kit: every shared component on one page so Nat can see them on a
 * phone and the axe scan can check them. Not linked from anywhere a player
 * would go once the real screens exist.
 */
export const Route = createFileRoute("/kit")({
  component: Kit,
});

const FIVE_MINUTES = 5 * 60_000;

function Kit() {
  const countdown = useCountdown(FIVE_MINUTES);
  const wakeLock = useWakeLock();
  const wakeId = useId();

  return (
    <main className="flex flex-col gap-8">
      <header>
        <h1 className="text-xl font-bold">{t("kit.title")}</h1>
        <p className="mt-2 text-sm text-ink-muted">{t("kit.intro")}</p>
      </header>

      <section aria-labelledby="kit-buttons" className="flex flex-col gap-3">
        <h2 id="kit-buttons" className="text-lg font-semibold">
          {t("kit.buttons")}
        </h2>
        <Button full>{t("kit.buttonPrimary")}</Button>
        <Button variant="secondary" full>
          {t("kit.buttonSecondary")}
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" className="flex-1">
            {t("kit.buttonGhost")}
          </Button>
          <Button variant="danger" className="flex-1">
            {t("kit.buttonDanger")}
          </Button>
        </div>
        <Button disabled full>
          {t("kit.buttonDisabled")}
        </Button>
      </section>

      <section aria-labelledby="kit-card" className="flex flex-col gap-3">
        <h2 id="kit-card" className="text-lg font-semibold">
          {t("kit.card")}
        </h2>
        <Card>{t("kit.cardBody")}</Card>
        <Card tone="sunken">{t("kit.cardBody")}</Card>
      </section>

      <section aria-labelledby="kit-timer" className="flex flex-col items-center gap-4">
        <h2 id="kit-timer" className="self-start text-lg font-semibold">
          {t("kit.timer")}
        </h2>
        <Timer totalMs={FIVE_MINUTES} remainingMs={countdown.remainingMs} />
        <div className="flex w-full gap-3">
          {countdown.running ? (
            <Button variant="secondary" className="flex-1" onClick={countdown.pause}>
              {t("kit.timerPause")}
            </Button>
          ) : (
            <Button className="flex-1" onClick={countdown.start}>
              {t("kit.timerStart")}
            </Button>
          )}
          <Button variant="ghost" className="flex-1" onClick={countdown.reset}>
            {t("kit.timerReset")}
          </Button>
        </div>
      </section>

      <section aria-labelledby="kit-wake" className="flex flex-col gap-3">
        <h2 id="kit-wake" className="text-lg font-semibold">
          {t("kit.wakeLock")}
        </h2>
        <Card>
          <label htmlFor={wakeId} className="flex min-h-target items-center justify-between gap-4">
            <span className="font-semibold">{t("kit.wakeLockToggle")}</span>
            <input
              id={wakeId}
              type="checkbox"
              role="switch"
              className="size-8 accent-ember"
              checked={wakeLock.wanted}
              disabled={!wakeLock.supported}
              onChange={(e) => wakeLock.setWanted(e.target.checked)}
            />
          </label>
          <p className="mt-2 text-sm text-ink-muted" aria-live="polite">
            {!wakeLock.supported
              ? t("kit.wakeLockUnsupported")
              : wakeLock.active
                ? t("kit.wakeLockOn")
                : t("kit.wakeLockOff")}
          </p>
        </Card>
      </section>

      <Link to="/" className="contents">
        <Button variant="secondary" full tabIndex={-1}>
          {t("kit.back")}
        </Button>
      </Link>
    </main>
  );
}
